import type {
  ChatMessage,
  DocumentRef,
  InstrumentRef,
  ThoughtStep,
} from 'features/chatbot/types/chat'
import type {
  ConversationMessage,
  InstrumentWithData,
  Source,
  ThinkingNote,
} from 'features/chatbot/types/conversation'

function documentRefToSource(doc: DocumentRef): Source {
  return {
    id: doc.id,
    text: doc.title || doc.drawingNumber || doc.id,
    data: {
      id: doc.id,
      title: doc.title,
      drawingNumber: doc.drawingNumber,
      documentType: doc.documentType,
      pageNumber: doc.pageNumber,
    },
  }
}

function instrumentRefToSource(inst: InstrumentRef): Source {
  return {
    id: `inst-${inst.id}`,
    text: inst.instrumentName,
    data: {
      instrumentName: inst.instrumentName,
      entryId: inst.id,
      startDatetime: inst.startDatetime,
      endDatetime: inst.endDatetime,
      piTag: inst.piTag,
    },
  }
}

function instrumentRefToWithData(inst: InstrumentRef): InstrumentWithData {
  return {
    instrumentName: inst.instrumentName,
    entryId: inst.id,
    startDatetime: inst.startDatetime,
    endDatetime: inst.endDatetime,
  }
}

function thoughtId(messageId: string, thought: ThoughtStep): string {
  return thought.id ?? `${messageId}:${thought.seq}`
}

function thoughtToNote(messageId: string, thought: ThoughtStep): ThinkingNote {
  const id = thoughtId(messageId, thought)
  const docSources =
    thought.references?.documents.map(documentRefToSource) ?? []
  const instSources =
    thought.references?.instruments.map(instrumentRefToSource) ?? []
  const sources = [...docSources, ...instSources]

  const text =
    thought.text ||
    (thought.toolCall ? `Calling ${thought.toolCall.name}` : '') ||
    thought.toolResultSummary ||
    ''

  // Preserve tool metadata so specialized renderers (e.g. proposal
  // cards) can detect the call after a page reload, not just on the
  // live stream.
  const toolCall = thought.toolCall
    ? { name: thought.toolCall.name, args: thought.toolCall.args }
    : undefined

  return {
    id,
    text,
    sources: sources.length > 0 ? sources : undefined,
    toolCall,
  }
}

export function mapBackendMessagesToConversation(
  messages: ChatMessage[]
): ConversationMessage[] {
  const out: ConversationMessage[] = []

  for (const msg of messages) {
    if (msg.role === 'user') {
      out.push({ type: 'userMessage', messageId: msg.id, text: msg.content })
      continue
    }

    if (msg.status === 'error' && !msg.content) {
      out.push({ type: 'error', text: 'Assistant message errored' })
      continue
    }

    const thinkingNotes = msg.thoughts
      .map((t) => thoughtToNote(msg.id, t))
      .filter((n) => n.text || (n.sources && n.sources.length > 0))

    const docSources = msg.allDocuments.map(documentRefToSource)
    const instSources = msg.allInstruments.map(instrumentRefToSource)
    const sources = [...docSources, ...instSources]
    const instrumentsWithData = msg.allInstruments.map(instrumentRefToWithData)

    out.push({
      type: 'aiResponse',
      messageId: msg.id,
      naturalLanguageResponseBySegments: [{ text: msg.content, citations: [] }],
      sources: sources.length > 0 ? sources : undefined,
      thinkingTimeSeconds: msg.thinkingTimeSeconds,
      thinkingNotes: thinkingNotes.length > 0 ? thinkingNotes : undefined,
      instrumentsWithData:
        instrumentsWithData.length > 0 ? instrumentsWithData : undefined,
    })
  }

  return out
}
